import { useEffect, useState } from "react";
import { formatDistanceStrict } from "date-fns";
import { ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PublicOffer } from "./publicOffer.types";
import { OFFER_CARD_BG } from "./publicOffer.utils";

export function OfferImagePlaceholder({
  label,
  variant = "default",
}: {
  label: string;
  variant?: "default" | "card";
}) {
  return (
    <div
      className={cn(
        "flex h-full w-full items-center justify-center px-4",
        variant === "card" ? "bg-slate-50" : "",
      )}
      style={
        variant === "card" ? undefined : { backgroundImage: OFFER_CARD_BG }
      }
      role="img"
      aria-label={label}
    >
      <ImageIcon
        className="h-11 w-11 text-slate-400/90"
        strokeWidth={1.25}
        aria-hidden
      />
    </div>
  );
}

export function OfferCardMedia({
  offer,
  imageUrl,
  hoverZoom = true,
  imageFit = "cover",
  variant = "default",
}: {
  offer: PublicOffer;
  /** When omitted, uses `cardImageUrl` (list) — pass `detailImageUrl` for the detail modal */
  imageUrl?: string;
  hoverZoom?: boolean;
  imageFit?: "cover" | "contain";
  variant?: "default" | "card";
}) {
  const alt = offer.imageAlt?.trim() || offer.title;
  const [imageFailed, setImageFailed] = useState(false);
  const resolvedImageUrl =
    imageUrl ?? (isCard ? offer.detailImageUrl || offer.cardImageUrl : offer.cardImageUrl);
  const showPhoto = Boolean(resolvedImageUrl) && !imageFailed;
  const isCard = variant === "card";

  return (
    <div
      className={cn(
        "relative w-full shrink-0 overflow-hidden border-b border-slate-100",
        isCard ? "aspect-[3/2] min-h-[200px] sm:min-h-[240px]" : "aspect-[16/10]",
        imageFit === "contain" && !isCard ? "bg-white" : "bg-slate-100",
        isCard && imageFit === "contain" && "bg-slate-50",
      )}
    >
      {showPhoto ? (
        <img
          src={resolvedImageUrl}
          alt={alt}
          loading="lazy"
          decoding="async"
          onError={() => setImageFailed(true)}
          className={cn(
            "h-full w-full duration-500 ease-out",
            imageFit === "contain" ? "object-contain" : "object-cover",
            hoverZoom && "transition-transform group-hover:scale-[1.03]",
          )}
        />
      ) : (
        <OfferImagePlaceholder label={alt} variant={isCard ? "card" : "default"} />
      )}

      {!isCard && (
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/10"
          aria-hidden
        />
      )}
    </div>
  );
}

export function OfferTimeLeftLabel({
  expiresAtMillis,
  variant = "default",
}: {
  expiresAtMillis?: number;
  variant?: "default" | "card";
}) {
  const [now, setNow] = useState(() => Date.now());
  const isCard = variant === "card";
  const unavailableClass = isCard
    ? "text-xs font-medium text-slate-500"
    : "text-xs font-medium text-slate-500";
  const expiredClass = isCard
    ? "text-xs font-semibold text-amber-700"
    : "text-xs font-semibold text-amber-700";
  const activeClass = isCard
    ? "text-xs font-medium text-slate-700"
    : "truncate text-xs font-semibold text-emerald-700";

  useEffect(() => {
    if (expiresAtMillis == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAtMillis]);

  if (expiresAtMillis == null) {
    return (
      <span className={unavailableClass}>Schedule not available</span>
    );
  }
  if (now >= expiresAtMillis) {
    return <span className={expiredClass}>This offer has expired</span>;
  }
  return (
    <span className={activeClass}>
      {formatDistanceStrict(new Date(now), new Date(expiresAtMillis))} left
    </span>
  );
}
